import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { Form, Formik, FormikHelpers, FormikProps } from 'formik'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { FriendlyError, LandThumbnail, LandThumbnailSkin, ResponsiveDialog } from '@cibo/ui'

import { ResourceDetail } from '@cibo/core'
import { object } from 'yup'
import { DetailModalState, GenericDetailEditor, GenericEditorSkeleton, TraitFeature } from '.'
import { useField, useSaveDetail } from '../..'

const THUMBNAIL_SIZE = 80

type EditSingleFieldProps = {
  requestClose(): void
} & DetailModalState

export const EditSingleFieldForm = ({
  filterInputs,
  onSuccess,
  requestClose,
  requirement,
  resourceId,
  traitId,
}: EditSingleFieldProps) => {
  const { t } = useTranslation('@cibo/landmanager/DetailEditing')
  const { enqueueSnackbar } = useSnackbar()

  const field = useField(resourceId)
  const fieldModels = field.data && [field.data]

  const saveDetail = useSaveDetail()
  const year = requirement?.year

  useEffect(() => {
    if (resourceId) {
      saveDetail.reset()
    }
  }, [resourceId])

  const Trait = traitId ? TraitFeature.forTraitId(traitId) : undefined
  const { t: traitT } = useTranslation(Trait?.ns)

  const handleSubmit = (values: ResourceDetail, formikHelpers: FormikHelpers<any>) => {
    if (!resourceId || !traitId) {
      return
    }

    const detailValue = Trait?.detailFromEditorValues
      ? Trait.detailFromEditorValues(values, requirement)
      : values

    saveDetail
      .mutateAsync({
        resourceId,
        details: [detailValue],
      })
      .then(() => {
        requestClose()
        enqueueSnackbar(t('updateSuccess', { trait: t(traitId, { ns: 'traits' }) }), {
          variant: 'success',
        })
        onSuccess && onSuccess()
        formikHelpers.resetForm()
      })
      .finally(() => {
        formikHelpers.setSubmitting(false)
      })
  }

  const initialValues = useMemo(
    () =>
      field.isLoading
        ? ({} as ResourceDetail)
        : ({
            input: Trait?.initialValues
              ? Trait.initialValues({ fieldModels: field.data ? [field.data] : [], year })
              : ({} as ResourceDetail),
            traitId,
            year,
          } as ResourceDetail),
    [year, field.isLoading, traitId]
  )

  const validationSchema = useMemo(
    () =>
      Trait?.validationSchema
        ? object().shape({
            input: Trait.validationSchema({ requirement, t: traitT }),
          })
        : undefined,
    [Trait, requirement, traitT]
  )
  const validator = (values: any) => {
    if (!Trait?.validate) return

    const input = Trait?.detailFromEditorValues
      ? Trait.detailFromEditorValues(values).input
      : values.input

    const { valid, errors } = Trait.validate(input)

    return valid ? {} : { input: errors }
  }

  const DetailEditor = !!Trait ? Trait.editor : GenericDetailEditor

  return (
    /**
     * You may be scratching your head looking at this component structure.  Here's the deal:
     * MUI Dialog scroll behavior requires that the <Dialog* /> compoonents be direct children of the <Dialog /> component.
     * The <DialogActions /> need access to the Formik state, but we don't want to render the Formik form until the fieldModels
     * have loaded, this means <Formik/> needs to wrap the entire dialog.  However, we want to show _something_ is happening when
     * the user clicks edit, so we disable the backdrop on the dialog and render a shared <Backdrop /> around the loading / loaded states.
     */
    <Backdrop sx={{ zIndex: theme => theme.zIndex.drawer + 1 }} open={!!resourceId}>
      {field.isFetching ? (
        <CircularProgress />
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          validate={validator}
          enableReinitialize
        >
          {({ dirty, isValid, submitForm }: FormikProps<any>) => (
            <Form>
              <ResponsiveDialog
                open={!!resourceId}
                onClose={requestClose}
                sx={{
                  '& .MuiInputLabel-root': { whiteSpace: 'unset' },
                }}
                maxWidth="sm"
                fullWidth
                hideBackdrop
              >
                <DialogTitle>{!!traitId && t('editField', { traitId })}</DialogTitle>
                <DialogContent>
                  <Stack spacing={1}>
                    {field.isLoading ? (
                      <Stack direction="row" spacing={2} p={1}>
                        <Skeleton
                          variant="rectangular"
                          height={THUMBNAIL_SIZE}
                          width={THUMBNAIL_SIZE}
                        />
                        <Stack sx={{ width: '100px' }}>
                          <Typography>
                            <Skeleton />
                          </Typography>
                          <Typography variant="caption">
                            <Skeleton />
                          </Typography>
                        </Stack>
                      </Stack>
                    ) : (
                      !!field.data && (
                        <Stack direction="row" spacing={2} alignItems="left" justifyContent="left">
                          <Box style={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }}>
                            <LandThumbnail
                              landProfile={field.data}
                              width={THUMBNAIL_SIZE}
                              height={THUMBNAIL_SIZE}
                              skin={LandThumbnailSkin.SMALL}
                            />
                          </Box>

                          <Stack>
                            <Typography>{field.data.name}</Typography>
                            <Typography variant="caption">{field.data.locationLabel}</Typography>
                          </Stack>
                        </Stack>
                      )
                    )}

                    {field.isLoading ? (
                      <GenericEditorSkeleton />
                    ) : !!traitId ? (
                      <DetailEditor
                        traitId={traitId}
                        name={'input'}
                        fieldModels={fieldModels || []}
                        //@ts-ignore this component may accept a year, but typing isn't apparently handling that conditionality
                        year={year}
                        requirement={requirement}
                        filterInputs={filterInputs}
                      />
                    ) : null}

                    {!!saveDetail.isError && (
                      <Stack direction="row" justifyContent="flex-end">
                        <FriendlyError message={t('problemSaving')} />
                      </Stack>
                    )}
                  </Stack>
                </DialogContent>

                <DialogActions sx={{ backgroundColor: 'background.default' }}>
                  <Box flex={1}>
                    {saveDetail.isError && (
                      <Alert severity="error" title={t('problemSaving')}></Alert>
                    )}
                  </Box>
                  <Button onClick={requestClose}>{t('cancel')}</Button>
                  <LoadingButton
                    variant="contained"
                    onClick={submitForm}
                    disabled={!dirty || !isValid}
                    loading={saveDetail.isLoading}
                    data-testid="save-detail-button"
                  >
                    {t('save')}
                  </LoadingButton>
                </DialogActions>
              </ResponsiveDialog>
            </Form>
          )}
        </Formik>
      )}
    </Backdrop>
  )
}
